<div class="modal-header border-bottom border-secondary">
  <h4 class="modal-title text-light pull-left">Developer Controls</h4>
  <button type="button" class="btn-close btn-close-white pull-right" aria-label="Close" (click)="close()">
    <span aria-hidden="true" class="visually-hidden">&times;</span>
  </button>
</div>

<div class="dev-controls modal-body bg-dark text-light">
  <!-- Tab Navigation -->
  <div class="tabs">
    <button
      class="tab-btn"
      [class.active]="activeTab === 'jobs'"
      (click)="activeTab = 'jobs'"
    >
      Job Controls
    </button>
    <button
      class="tab-btn"
      [class.active]="activeTab === 'settings'"
      (click)="activeTab = 'settings'"
    >
      Developer Options
    </button>
  </div>

  <!-- Job Controls Tab -->
  @if (activeTab === 'jobs') {
    <p class="subtitle">Manually trigger a pipeline job and watch it run to completion.</p>

    @if (jobs.length) {
      <div class="job-grid">
        @for (job of jobs; track job.key) {
          <div class="job-card">
            <div class="job-card-header">
              <span class="job-name">{{ job.name }}</span>
              <button
                class="run-btn"
                [disabled]="triggeringKeys.has(job.key)"
                (click)="trigger(job)"
              >
                {{ triggeringKeys.has(job.key) ? 'Starting…' : 'Run' }}
              </button>
            </div>
            <p class="job-description">{{ job.description }}</p>
            <code class="job-route">{{ job.route }}</code>
          </div>
        }
      </div>
    } @else {
      @if (!loadError) {
        <p>Loading jobs…</p>
      } @else {
        <p class="error">{{ loadError }}</p>
      }
    }
  }

  <!-- Developer Options Tab -->
  @if (activeTab === 'settings') {
    <div class="settings-section">
      <h3>Developer Options</h3>

      <div class="option-group">
        <div class="option-row">
          <label for="azure-server-toggle" class="option-label">
            <span>Use Azure Server</span>
            <p class="option-description">Toggle between local and Azure server endpoints. Azure is used by default; turn off to target your local server.</p>
          </label>
          <input
            id="azure-server-toggle"
            type="checkbox"
            class="toggle-checkbox"
            [checked]="useAzureServer"
            (change)="toggleAzureServer()"
          />
        </div>

        <div class="option-row">
          <label for="inactivity-toggle" class="option-label">
            <span>Inactivity Timer</span>
            <p class="option-description">Automatically log out after inactivity period.</p>
          </label>
          <input
            id="inactivity-toggle"
            type="checkbox"
            class="toggle-checkbox"
            [checked]="inactivityTimerEnabled"
            (change)="toggleInactivityTimer()"
          />
        </div>

        <div class="option-row">
          <label for="inactivity-timeout" class="option-label">
            <span>Inactivity Timeout (minutes)</span>
            <p class="option-description">How many minutes before auto-logout. Default is 15 minutes.</p>
          </label>
          <input
            id="inactivity-timeout"
            type="number"
            min="1"
            max="120"
            [(ngModel)]="inactivityTimeoutMinutes"
            (change)="updateInactivityTimeout()"
            style="width: 80px; padding: 6px 8px; border: 1px solid #374151; border-radius: 4px; background: #1f2937; color: #e5e7eb;"
          />
        </div>

        <div class="option-row">
          <label for="force-offline-toggle" class="option-label">
            <span>Force Offline Mode</span>
            <p class="option-description">Simulate offline connectivity for testing.</p>
          </label>
          <input
            id="force-offline-toggle"
            type="checkbox"
            class="toggle-checkbox"
            [checked]="forceOffline"
            (change)="toggleForceOffline()"
          />
        </div>
      </div>

      <div class="option-group">
        <button class="check-sync-btn" (click)="openSyncCheck()">
          Check Data Sync
        </button>
        <p class="option-description">Verify that data synchronization is working correctly.</p>
      </div>
    </div>
  }

  @if (activeRuns.length) {
    <div class="active-runs">
      <h3>Active &amp; Recent Runs</h3>

      @for (run of activeRuns; track trackByRun($index, run)) {
        <div class="run-card">
          <div class="run-card-header">
            <span class="status-dot" [ngClass]="run.status"></span>
            <span class="run-job-name">{{ run.jobName }}</span>
            @if (run.runId) {
              <span class="run-id">run: {{ run.runId }}</span>
            }
            <span class="run-elapsed">{{ elapsedSeconds(run) }}s</span>
            <span class="run-status-label" [ngClass]="run.status">{{ run.status }}</span>
            <button class="dismiss-btn" (click)="dismiss(run)" title="Dismiss">&times;</button>
          </div>

          @if (run.status === 'no-progress') {
            <p class="no-progress-note">
              Job triggered but no run_id was returned — live progress isn't available for this endpoint.
            </p>
          }

          @if (run.error) {
            <p class="error-note">{{ run.error }}</p>
          }

          @if (run.runId) {
            <div class="log-console">
              @for (line of run.lines; track line._id) {
                <div class="log-line" [ngClass]="line.level | lowercase">
                  <span class="log-ts">{{ line.ts | date: 'HH:mm:ss' }}</span>
                  <span class="log-level">{{ line.level }}</span>
                  <span class="log-message">{{ line.message }}</span>
                </div>
              } @empty {
                <div class="log-empty">Waiting for log output…</div>
              }
            </div>
          }

          @if (run.summary) {
            <details class="run-summary">
              <summary>Summary</summary>
              <pre>{{ run.summary | json }}</pre>
            </details>
          }
        </div>
      }
    </div>
  }
</div>

<div class="modal-footer border-top border-secondary bg-dark">
  <button type="button" class="btn btn-secondary" (click)="close()">Close</button>
</div>
